import React, { useEffect, useState } from "react";
import { useAppSelector, useAppDispatch } from "redux/hooks";
import SmartService from "services/smart.service";
import Timeline, {
  DateHeader,
  SidebarHeader,
  TimelineHeaders,
  TodayMarker,
} from "react-calendar-timeline";
import "react-calendar-timeline/lib/Timeline.css";
import moment from "moment-timezone";
import { MODAL, modalActions } from "redux/slices/modal";
import CircleLoader from "components/atoms/CircleLoader";
import { styled } from "@mui/material/styles";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import CalendarFilters from "./CalendarFilters";
import { Box, Container } from "@mui/material";
import GoBack from "components/atoms/GoBack";
import Banner from "components/templates/Banner";

let timezone = moment().tz("Asia/Riyadh").format("YYYY-MM-DD HH:mm:ss");
const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));

const timezone3 = {
  name: "Asia/Riyadh",
  ft: "+03:00",
};

const Calendar = ({ zone }: any) => {
  // const [focus, setFocus] = useState(0);
  const [show, setShow] = useState(false);

  useEffect(() => {
    moment.tz.setDefault("Asia/Riyadh");
    return () => {
      moment.tz.setDefault();
    };
  }, []);

  const loading = useAppSelector((state) => state.smart.loading);
  const refresh = useAppSelector((state) => state.smart.refresh);
  const shift: any = useAppSelector((state) => state.smart.shift);
  const { items = [], groups = [] }: any = useAppSelector(
    (state) => state.smart.timeLine
  );
  const loadingm = useAppSelector((state) => state.modal.loading);
  const calendar_filters = useAppSelector(
    (state) => state.smart.calendar_filters
  );

  const dispatch = useAppDispatch();

  let source: any = {
    BLACKLANE: { background: "black", color: "white" },
    IYELO: { background: "#ffce00", color: "black" },
    TRANSFEERO: { background: "#f9b442", color: "black" },
  };

  const CITY_TRIP_STYLE = { background: "#520A76", color: "#fff" };
  const LONG_DISTANCE_KM = 150;

  // Purple marks either a city trip or any long-haul booking (>= 150 km),
  // whatever its trip type. Deliberately not the revenue report's city-to-city
  // rule, which uses 100 km and reclassifies short city trips as plain trips.
  const getItemColors = (item: any) => {
    const distance = Number(item.distance_in_km);
    if (
      item.booking_type === "city_trip" ||
      (Number.isFinite(distance) && distance >= LONG_DISTANCE_KM)
    ) {
      return CITY_TRIP_STYLE;
    }
    return source[item.source] || {};
  };

  useEffect(() => {
    SmartService.getTimeLine(calendar_filters);
  }, [refresh, calendar_filters]);

  const list = items?.map((item: any, index: number) => ({
    id: index + 1,
    main_id: item.main_id || item.source_id,
    group: item.group,
    source: item.source,
    title: moment(item.date).format("HH:mm"),
    booking_number: item.booking_number,
    start_time: moment(item.date),
    end_time: moment(item.date).add(
      item.source === "BLACKLANE" || item.source === "TRANSFEERO"
        ? Number(item.duration) / 60
        : Number(item.duration),
      "hours"
    ),
    // end_time: moment(item.date).add(Number(item.duration), "hours"),
    duration:
      item.source === "BLACKLANE" || item.source === "TRANSFEERO"
        ? (Number(item.duration) / 60)?.toFixed(2)
        : Number(item.duration)?.toFixed(2),
    // duration: (Number(item.duration))?.toFixed(0),
    canMove: false,
    pickup: item.pickup,
    destination: item.destination,
    booking_type: item.booking_type,
    distance_in_km: item.distance_in_km,
    itemProps: {
      className: `${
        item.airport_pickup
          ? "rct-item-border-left"
          : item.airport_dropOff
          ? "rct-item-border-right"
          : ""
      }`,
      style: {
        border: "none",
        // borderBottom: "none",
        borderLeft: item.airport_pickup ? "8px solid #2860F5" : "none",
        borderRight: item.airport_dropOff ? "8px solid #2860F5" : "none",
        ...getItemColors(item),
      },
    },
  }));

  let liId = list.length || 0;

  const setTime = (dt: any, time: any) => {
    const date = `${dt}T${time}:00${timezone3.ft}`;
    return moment(date);
  };
  let shiftList = shift?.flatMap((item: any, index: number) => {
    let groupId = groups?.find(
      (group: any) => group.driver_id == item.driver_id
    )?.id;

    let sList: any = [];

    liId++;

    item.slots.forEach((slot: any) => {
      liId++;
      sList.push({
        id: liId,
        driver_id: item.driver_id,
        everyday: item.everyday,
        main_id: item._id,
        main_start_date: item.start_date,
        slot_id: slot._id,
        source: "SHIFT",
        title: slot.reason || "",
        group: groupId,
        s_time: slot.start_time,
        e_time: slot.end_time,
        s_date: slot.start_date,
        e_date: slot.end_date,
        start_time: setTime(
          moment(slot.start_date).format("YYYY-MM-DD"),
          slot.start_time
        ),
        end_time: setTime(
          moment(slot.end_date).format("YYYY-MM-DD"),
          slot.end_time
        ),
        canMove: false,
        itemProps: {
          style: {
            border: "1px solid  #630330",
            background: "transparent",
            textDecorationLine: "line-through",
            position: "relative",
            color: "#630330",
            width: "100%",
          },
        },
      });
    });

    return sList;
  });

  const minTime = moment().tz("Asia/Riyadh").startOf("day").valueOf();
  const maxTime = moment()
    .tz("Asia/Riyadh")
    .add(1, "months")
    .endOf("month")
    .valueOf();
  const handleTimeChange = (
    visibleTimeStart: any,
    visibleTimeEnd: any,
    updateScrollCanvas: any
  ) => {
    if (visibleTimeStart < minTime && visibleTimeEnd > maxTime) {
      updateScrollCanvas(minTime, maxTime);
    } else if (visibleTimeStart < minTime) {
      updateScrollCanvas(
        minTime,
        minTime + (visibleTimeEnd - visibleTimeStart)
      );
    } else if (visibleTimeEnd > maxTime) {
      updateScrollCanvas(
        maxTime - (visibleTimeEnd - visibleTimeStart),
        maxTime
      );
    } else {
      updateScrollCanvas(visibleTimeStart, visibleTimeEnd);
    }
  };
  const moveResizeValidator = (action: any, item: any, time: any) => {
    if (time < new Date(timezone).getTime()) {
      const newTime =
        Math.ceil(new Date(timezone).getTime() / (15 * 60 * 1000)) *
        (15 * 60 * 1000);
      return newTime;
    }
    return time;
  };

  const onClickDetails = (itemId: any, type?: string) => {
    let data: any;
    if (type === "desktop") {
      data = itemId;
    } else {
      data = [...list, ...shiftList]?.find((item: any) => item.id === itemId);
    }
    console.log("datadatadata", data);
    if (data.source === "SHIFT") {
      dispatch(
        modalActions.openModal({
          width: "500px",
          closeBackdropClick: true,
          type: MODAL.Add_SHIFT,
          data: { shift: data, update: true },
        })
      );
    } else {
      dispatch(
        modalActions.openModal({
          width: "600px",
          closeBackdropClick: true,
          type: MODAL.BOOKING_DETAILS,
          data: {
            _id: data?.main_id || data?.source_id,
            source: data?.source,
            booking_number: data?.booking_number,
          },
        })
      );
    }
  };
  const ItemRenderer = ({
    item,
    timelineContext,
    itemContext,
    getItemProps,
    getResizeProps,
  }: any) => {
    const { left: leftResizeProps, right: rightResizeProps } = getResizeProps();

    return (
      <div
        onMouseEnter={() => setShow(item.id)}
        onMouseLeave={() => setShow(false)}
        {...getItemProps({
          style: item.itemProps.style,
        })}
        onClick={() => onClickDetails(item, "desktop")}
      >
        {itemContext.useResizeHandle ? <div {...leftResizeProps} /> : null}

        <div
          style={{
            height: itemContext.dimensions.height,
            overflow: "hidden",
            paddingLeft: 3,
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            position: "relative",
          }}
          className={item.source == "SHIFT" ? "rct-custom-text" : ""}
        >
          {/* {itemContext.title} */}
          <div>
            <LightTooltip
              open={show === item.id && item.source != "SHIFT"}
              placement="top-start"
              title={
                <div style={{ fontSize: "14px" }}>
                  Time: {itemContext.title}
                  <br />
                  Pickup: {item.pickup}
                  <br />
                  {item.booking_type === "hourly" ? (
                    <>{item.duration} hours</>
                  ) : (
                    <>Destination: {item.destination}</>
                  )}
                </div>
              }
            >
              <div>{itemContext.title}</div>
            </LightTooltip>
          </div>
        </div>

        {itemContext.useResizeHandle ? <div {...rightResizeProps} /> : null}
      </div>
    );
  };

  let num = loadingm ? 1001 : 1000;
  return (
    // return groups?.length === 0 || list?.length === 0 ? (
    //   <>loading...</>
    // ) : (
    <Box sx={{ padding: "0px 16px 16px 16px" }}>
      {loading && <CircleLoader />}
      <GoBack path="/bookings" title="Back to Bookings" />
      <Banner heading="Calendar" />
      <CalendarFilters />
      <Timeline
        groups={groups || []}
        items={[...list, ...shiftList]}
        minZoom={60 * num}
        // maxZoom={1000 * 60 * 60 * 24 * 365.25}

        itemRenderer={ItemRenderer}
        defaultTimeStart={moment(timezone).subtract(2, "hours").startOf("hour")}
        defaultTimeEnd={moment(timezone).add(10, "hours").endOf("hour")}
        moveResizeValidator={moveResizeValidator}
        onTimeChange={handleTimeChange}
        // onItemSelect={(itemId) => onClickDetails(itemId)}
        onItemClick={(itemId) => onClickDetails(itemId)}
        itemTouchSendsClick
        groupRenderer={({ group }) => {
          return (
            <button
              type="button"
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
              }}
              onClick={() => {
                if (group.driver_id) {
                  dispatch(
                    modalActions.openModal({
                      width: "500px",
                      type: MODAL.Add_SHIFT,
                      data: {
                        shift: {
                          driver: group.title,
                          driver_id: group.driver_id,
                        },
                        zone,
                      },
                    })
                  );
                }
              }}
            >
              {group.title}
            </button>
          );
        }}
        selected={[]}
        timeSteps={{
          minute: 5,
          second: 1,
          hour: 1,
          day: 1,
          month: 1,
          year: 1,
        }}
        stackItems
        horizontalLineClassNamesForGroup={(group: any) => {
          return group.id > 2000000 ? ["empty-rct-row-1", "empty-rct-row"] : [];
        }}

        //  className="rct-sticky"
        // itemHeightRatio={0.75}
      >
        <TodayMarker date={new Date()}>
          {({ styles, date }) => <div style={{ ...styles, zIndex: 40 }} />}
        </TodayMarker>

        <TimelineHeaders className="rct-sticky">
          <SidebarHeader>
            {({ getRootProps }) => {
              return (
                <div {...getRootProps()}>
                  <span
                    style={{
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                    }}
                  >
                    Drivers
                  </span>
                </div>
              );
            }}
          </SidebarHeader>
          <DateHeader unit="primaryHeader" />
          <DateHeader />
        </TimelineHeaders>
      </Timeline>
    </Box>
  );
};

export default Calendar;
